import { useEffect, useState } from "react";
import {
  SurfCloudAPI,
  cleanSurfSource,
  extractPdfText,
  extractWebUrl,
  researchSurfSource,
  searchWeb,
  type SurfNoteSummary,
} from "@vida/surf-core";
import {
  createPdfSourceConfig,
  createSurfApiConfig,
  createWebProxyConfig,
  ensureSurfClientConfig,
} from "../../services/surfClientConfig";

type ContentTab = "paste" | "pdf" | "note" | "url" | "research";

type Props = {
  sourceText: string;
  onSourceChange: (text: string) => void;
  sessionNoteId: string;
};

export function ContentStep({ sourceText, onSourceChange, sessionNoteId }: Props) {
  const [tab, setTab] = useState<ContentTab>("paste");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<SurfNoteSummary[]>([]);
  const [urlInput, setUrlInput] = useState("");
  const [researchQ, setResearchQ] = useState("");
  const [researchLog, setResearchLog] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);

  useEffect(() => {
    void ensureSurfClientConfig().then(async () => {
      try {
        const cfg = createSurfApiConfig();
        const page = await SurfCloudAPI.listNotes(cfg, 30);
        setNotes((page.items ?? []).filter((n) => !n.deleted));
      } catch {
        /* notes optional */
      }
    });
  }, []);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await ensureSurfClientConfig();
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="wizard-panel">
      <h2>Contenuto</h2>
      <p className="muted">Aggiungi materiale sorgente per l&apos;outline.</p>

      <div className="wizard-tabs">
        {(
          [
            ["paste", "Markdown"],
            ["pdf", "PDF"],
            ["note", "Nota"],
            ["url", "URL"],
            ["research", "Ricerca AI"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={tab === id ? "tab active" : "tab"}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "paste" && (
        <textarea
          className="source-area"
          rows={14}
          value={sourceText}
          placeholder="Incolla markdown o testo…"
          onChange={(e) => onSourceChange(e.target.value)}
        />
      )}

      {tab === "pdf" && (
        <div className="wizard-block">
          <input
            type="file"
            accept="application/pdf,.pdf"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              void run(async () => {
                const text = await extractPdfText(createPdfSourceConfig(), file, sessionNoteId);
                onSourceChange(sourceText ? `${sourceText}\n\n---\n\n${text}` : text);
              });
            }}
          />
          <p className="muted">Il PDF viene indicizzato nel cloud e il testo estratto appare sotto.</p>
        </div>
      )}

      {tab === "note" && (
        <div className="wizard-block">
          <ul className="note-list">
            {notes.map((n) => (
              <li key={n.noteId}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const cfg = createSurfApiConfig();
                      const note = await SurfCloudAPI.getNote(cfg, n.noteId);
                      if (!note?.content?.trim()) throw new Error("Nota vuota.");
                      const chunk = `# ${note.title}\n\n${note.content.trim()}`;
                      onSourceChange(sourceText ? `${sourceText}\n\n---\n\n${chunk}` : chunk);
                    })
                  }
                >
                  {n.title}
                </button>
              </li>
            ))}
          </ul>
          {notes.length === 0 && <p className="muted">Nessuna nota cloud trovata.</p>}
        </div>
      )}

      {tab === "url" && (
        <div className="wizard-block">
          <div className="row">
            <input
              type="url"
              className="text-input"
              placeholder="https://…"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
            />
            <button
              type="button"
              className="primary-btn"
              disabled={busy || !urlInput.trim()}
              onClick={() =>
                void run(async () => {
                  try {
                    const { markdown, title } = await extractWebUrl(
                      createWebProxyConfig(),
                      urlInput.trim(),
                    );
                    const chunk = title ? `# ${title}\n\n${markdown}` : markdown;
                    onSourceChange(sourceText ? `${sourceText}\n\n---\n\n${chunk}` : chunk);
                  } catch (extractErr) {
                    let hostname = urlInput.trim();
                    try {
                      hostname = new URL(urlInput.trim()).hostname;
                    } catch {
                      /* use raw input */
                    }
                    const results = await searchWeb(
                      createWebProxyConfig(),
                      hostname,
                      4,
                    );
                    if (!results.length) throw extractErr;
                    const fallback = results
                      .map((r) => `## ${r.title}\n${r.url}\n\n${r.content}`)
                      .join("\n\n---\n\n");
                    onSourceChange(
                      sourceText
                        ? `${sourceText}\n\n---\n\n${fallback}`
                        : fallback,
                    );
                  }
                })
              }
            >
              Estrai
            </button>
          </div>
        </div>
      )}

      {tab === "research" && (
        <div className="wizard-block">
          <div className="research-log">
            {researchLog.map((m, i) => (
              <p key={i} className={m.role === "user" ? "research-user" : "research-ai"}>
                <strong>{m.role === "user" ? "Tu" : "AI"}:</strong> {m.content}
              </p>
            ))}
          </div>
          <div className="row">
            <input
              className="text-input"
              placeholder="Domanda sul materiale…"
              value={researchQ}
              onChange={(e) => setResearchQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && researchQ.trim() && sourceText.trim()) {
                  e.preventDefault();
                  void run(async () => {
                    const q = researchQ.trim();
                    const answer = await researchSurfSource(
                      createSurfApiConfig(),
                      sourceText,
                      q,
                      researchLog,
                    );
                    setResearchLog((prev) => [
                      ...prev,
                      { role: "user", content: q },
                      { role: "assistant", content: answer },
                    ]);
                    setResearchQ("");
                    onSourceChange(`${sourceText.trim()}\n\n## Ricerca\n\n**Q:** ${q}\n\n${answer}`);
                  });
                }
              }}
            />
            <button
              type="button"
              disabled={busy || !researchQ.trim() || !sourceText.trim()}
              onClick={() =>
                void run(async () => {
                  const q = researchQ.trim();
                  const answer = await researchSurfSource(
                    createSurfApiConfig(),
                    sourceText,
                    q,
                    researchLog,
                  );
                  setResearchLog((prev) => [
                    ...prev,
                    { role: "user", content: q },
                    { role: "assistant", content: answer },
                  ]);
                  setResearchQ("");
                  onSourceChange(`${sourceText.trim()}\n\n## Ricerca\n\n**Q:** ${q}\n\n${answer}`);
                })
              }
            >
              Chiedi
            </button>
          </div>
          {!sourceText.trim() && (
            <p className="muted">Incolla prima del materiale sorgente (tab Markdown).</p>
          )}
        </div>
      )}

      {tab !== "paste" && (
        <textarea
          className="source-area"
          rows={10}
          value={sourceText}
          readOnly
          placeholder="Il testo raccolto apparirà qui…"
        />
      )}

      <div className="wizard-actions">
        <button
          type="button"
          disabled={busy || !sourceText.trim()}
          onClick={() =>
            void run(async () => {
              const cleaned = await cleanSurfSource(createSurfApiConfig(), sourceText);
              onSourceChange(cleaned);
            })
          }
        >
          Pulisci con AI
        </button>
        {busy && <span className="muted">Elaborazione…</span>}
      </div>

      {error && <p className="error">{error}</p>}
    </section>
  );
}
